import { Grid } from "@mui/material";
import InsightCardRisks from "./InsightCardRisks";
import InsightCardCompliance from "./InsightCardCompliance";
import InsightCardClauses from "./InsightCardClauses";
import { INSIGHTS_ANALYSIS_TAB_ITEMS } from "../types";
import { useGetContractQuery } from "../../../../../../redux/slices/indexApiSlice";
import { useParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { IContract, ILogicalCheck } from "../../../../../../types/contract";

export const getAllLogicalChecks = (
  contract: IContract
): {
  component: string;
  risks: ILogicalCheck | undefined;
}[] => {
  const newLogicalChecks: {
    component: string;
    risks: ILogicalCheck | undefined;
  }[] = [];

  newLogicalChecks.push({
    component: "Financials",
    risks: contract.budget?.logicalCheck,
  });

  newLogicalChecks.push({
    component: "End date",
    risks: contract.dates?.logicalCheck,
  });

  newLogicalChecks.push({
    component: "Notice period",
    risks: contract.noticePeriod?.logicalCheck,
  });

  newLogicalChecks.push({
    component: "Vendor",
    risks: contract.vendor?.logicalCheck,
  });

  // also add each deviation's logical check
  contract.deviations?.forEach((deviation) => {
    if (deviation.logicalCheck) {
      newLogicalChecks.push({
        component: deviation.details?.article.title || "Deviation",
        risks: deviation.logicalCheck,
      });
    }
  });

  // also add each prompt's logical check
  contract.prompts?.forEach((prompt) => {
    if (prompt.logicalCheck) {
      newLogicalChecks.push({
        component: prompt.promptTitle || "Prompt",
        risks: prompt.logicalCheck,
      });
    }
  });

  // add each task's logical check
  contract.tasks?.forEach((task) => {
    if (task.logicalCheck?.logic_check) {
      newLogicalChecks.push({
        component: task.details?.title || "Task",
        risks: task.logicalCheck,
      });
    }
  });

  // only keep the false logical checks
  const filteredLogicalChecks = newLogicalChecks.filter(
    (check) => check.risks && check.risks.logic_check?.is_logical === false
  );

  return filteredLogicalChecks;
};

export const getContractRisks = (
  contract: IContract
): {
  high: number;
  medium: number;
  low: number;
} => {
  const newLogicalChecks = getAllLogicalChecks(contract);

  // return {
  //   high: newLogicalChecks.filter(
  //     (check) => check?.logic_check?.risk === "high"
  //   ).length,
  //   medium: newLogicalChecks.filter(
  //     (check) => check?.logic_check?.risk === "medium"
  //   ).length,
  //   low: newLogicalChecks.filter((check) => check?.logic_check?.risk === "low")
  //     .length,
  // };

  // count all logical checks as medium risk for now
  const total = newLogicalChecks.reduce(
    (acc, curr) => {
      if (curr.risks?.logic_check?.is_logical === false) {
        acc.medium += 1;
      }

      // if (curr.risks?.logic_check?.risk === "high") {
      //   acc.high += 1;
      // } else if (curr.risks?.logic_check?.is_logical === false) {
      //   acc.medium += 1;
      // } else if (curr.risks?.logic_check?.risk === "low") {
      //   acc.low += 1;
      // }
      return acc;
    },
    { high: 0, medium: 0, low: 0 }
  );

  return total;
};

const InsightsSummaryCards = ({
  onClick,
}: {
  onClick: (type: INSIGHTS_ANALYSIS_TAB_ITEMS) => void;
}) => {
  const { t } = useTranslation();
  const { contractId } = useParams();
  const { data: contract } = useGetContractQuery(contractId ?? skipToken);
  const [riskLabel, setRiskLabel] = useState<string>("");
  const [complianceScore, setComplianceScore] = useState<number>(0);
  const [majorDeviations, setMajorDeviations] = useState<number>(0);

  const [risks, setRisks] = useState<{
    high: number;
    medium: number;
    low: number;
  }>({
    high: 0,
    medium: 0,
    low: 0,
  });

  useEffect(() => {
    if (contract) {
      const newRisks = getContractRisks(contract);
      setRisks(newRisks);
    }
  }, [contract]);

  useEffect(() => {
    if (contract?.deviations && contract.deviations.length > 0) {
      const newMax = contract.deviations?.length * 10;

      const newTotal = contract.deviations?.reduce(
        (acc, curr) => acc + (curr.details?.score ?? 10),
        0
      );

      const perc = (newTotal / newMax) * 100;
      const roundedPerc = Math.round(perc * 100) / 100;
      setComplianceScore((Math.round(100 - roundedPerc) * 10) / 10);

      if (roundedPerc > 90) {
        setRiskLabel("Very high risk");
      } else if (roundedPerc > 70) {
        setRiskLabel("High risk");
      } else if (roundedPerc > 30) {
        setRiskLabel("Medium risk");
      } else if (roundedPerc > 10) {
        setRiskLabel("Low risk");
      } else {
        setRiskLabel("Very low risk");
      }

      setMajorDeviations(
        contract.deviations.filter(
          (d) => d.details?.score && d.details?.score > 5
        ).length
      );
    }
  }, [contract]);

  return (
    <Grid container spacing={2}>
      <Grid size={4}>
        <InsightCardRisks
          title={t("Risks Detected")}
          risks={risks}
          onClick={() => onClick(INSIGHTS_ANALYSIS_TAB_ITEMS.RISK_ALL)}
        />
      </Grid>

      <Grid size={4}>
        <InsightCardCompliance
          title={t("Compliance Score")}
          complianceScore={complianceScore}
          deviations={majorDeviations}
          onClick={() => onClick(INSIGHTS_ANALYSIS_TAB_ITEMS.SCORES_V2)}
        />
      </Grid>

      <Grid size={4}>
        <InsightCardClauses
          title={t("Clauses Extracted")}
          clauses={{
            found:
              contract?.prompts?.filter(
                (prompt) => prompt.extractionResult?.infoFound === true
              ).length || 0,
            notFound:
              contract?.prompts?.filter(
                (prompt) => prompt.extractionResult?.infoFound !== true
              ).length || 0,
          }}
          onClick={() => onClick(INSIGHTS_ANALYSIS_TAB_ITEMS.FIELDS_FOUND)}
        />
      </Grid>
    </Grid>
  );
};

export default InsightsSummaryCards;
